import React, { useState } from 'react';
import { isHiddenField } from './sandboxConfig';

function filterHiddenFields(data) {
  if (Array.isArray(data)) return data.map(filterHiddenFields);
  if (data !== null && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data)
        .filter(([key]) => !isHiddenField(key))
        .map(([key, value]) => [key, filterHiddenFields(value)]),
    );
  }
  return data;
}

function JsonValue({ value, depth, defaultExpandDepth }) {
  if (value === null) return <span className="text-surface-400 italic">null</span>;
  if (typeof value === 'boolean') return <span className="text-warning-dark">{String(value)}</span>;
  if (typeof value === 'number') return <span className="text-success-dark">{value}</span>;
  if (typeof value === 'string') return <span className="text-accent-700">&quot;{value}&quot;</span>;
  if (Array.isArray(value)) {
    return <JsonArray items={value} depth={depth} defaultExpandDepth={defaultExpandDepth} />;
  }
  if (typeof value === 'object') {
    return <JsonObject data={value} depth={depth} defaultExpandDepth={defaultExpandDepth} />;
  }
  return <span>{String(value)}</span>;
}

function JsonArray({ items, depth, defaultExpandDepth }) {
  const [expanded, setExpanded] = useState(depth < defaultExpandDepth);

  if (items.length === 0) return <span className="text-surface-300">[]</span>;

  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} className="text-left hover:bg-surface-100 rounded px-0.5 -mx-0.5">
        <span className="text-surface-300">[</span>
        <span className="text-surface-400 text-xs mx-1">...{items.length} items</span>
        <span className="text-surface-300">]</span>
      </button>
    );
  }

  return (
    <span>
      <button type="button" onClick={() => setExpanded(false)} className="text-surface-300 hover:text-surface-500">[</button>
      <div className="ml-5">
        {items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="hover:bg-surface-50 rounded -mx-1 px-1">
            <JsonValue value={item} depth={depth + 1} defaultExpandDepth={defaultExpandDepth} />
            {i < items.length - 1 && <span className="text-surface-300">,</span>}
          </div>
        ))}
      </div>
      <span className="text-surface-300">]</span>
    </span>
  );
}

function JsonObject({ data, depth, defaultExpandDepth }) {
  const [expanded, setExpanded] = useState(depth < defaultExpandDepth);
  const entries = Object.entries(data);

  if (entries.length === 0) return <span className="text-surface-300">{'{}'}</span>;

  if (!expanded) {
    return (
      <button type="button" onClick={() => setExpanded(true)} className="text-left hover:bg-surface-100 rounded px-0.5 -mx-0.5">
        <span className="text-surface-300">{'{'}</span>
        <span className="text-surface-400 text-xs mx-1">...{entries.length} keys</span>
        <span className="text-surface-300">{'}'}</span>
      </button>
    );
  }

  return (
    <span>
      <button type="button" onClick={() => setExpanded(false)} className="text-surface-300 hover:text-surface-500">{'{'}</button>
      <div className="ml-5">
        {entries.map(([key, value], i) => (
          <div key={key} className="hover:bg-surface-50 rounded -mx-1 px-1">
            <span className="text-surface-500">&quot;{key}&quot;</span>
            <span className="text-surface-300">: </span>
            <JsonValue value={value} depth={depth + 1} defaultExpandDepth={defaultExpandDepth} />
            {i < entries.length - 1 && <span className="text-surface-300">,</span>}
          </div>
        ))}
      </div>
      <span className="text-surface-300">{'}'}</span>
    </span>
  );
}

function JsonSyntaxHighlighter({
  data, defaultExpandDepth = 2, expandKey, filterHidden = true,
}) {
  const maybeFiltered = filterHidden ? filterHiddenFields(data) : data;

  return (
    <pre className="font-mono text-[13px] leading-relaxed whitespace-pre">
      <JsonValue
        key={expandKey}
        value={maybeFiltered}
        depth={0}
        defaultExpandDepth={defaultExpandDepth}
      />
    </pre>
  );
}

export default JsonSyntaxHighlighter;
